import { useEffect, useState } from 'react';
import { X, Unlock, Lock } from 'lucide-react';
import SmallLoader from '../../../Common/components/SmallLoader';
import { cn } from '../../../lib/utils';
import { LibraryItem } from './hooks/useLibrary';

interface ResizeModalProps {
    asset: LibraryItem;
    onClose: () => void;
    onResize: (width: number, height: number) => Promise<void>;
}

export default function ResizeModal({
    asset,
    onClose,
    onResize,
}: ResizeModalProps) {
    const [width, setWidth] = useState<number>(0);
    const [height, setHeight] = useState<number>(0);
    const [originalWidth, setOriginalWidth] = useState<number>(0);
    const [originalHeight, setOriginalHeight] = useState<number>(0);
    const [aspectRatioLocked, setAspectRatioLocked] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isResizing, setIsResizing] = useState(false);
    const [error, setError] = useState<string>('');

    // Load image dimensions
    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            setOriginalWidth(img.width);
            setOriginalHeight(img.height);
            setWidth(img.width);
            setHeight(img.height);
            setIsLoading(false);
        };
        img.onerror = () => {
            setError('Failed to load image dimensions');
            setIsLoading(false);
        };
        img.src = asset.url;
    }, [asset.url]);

    const aspectRatio = originalWidth / originalHeight;

    const handleWidthChange = (newWidth: number) => {
        setWidth(newWidth);
        if (aspectRatioLocked && aspectRatio) {
            setHeight(Math.round(newWidth / aspectRatio));
        }
    };

    const handleHeightChange = (newHeight: number) => {
        setHeight(newHeight);
        if (aspectRatioLocked && aspectRatio) {
            setWidth(Math.round(newHeight * aspectRatio));
        }
    };

    const handleResize = async () => {
        // Validation
        if (width <= 0 || height <= 0) {
            setError('Width and height must be positive numbers');
            return;
        }

        // Prevent upscaling
        if (width > originalWidth || height > originalHeight) {
            setError(
                'Cannot upscale image. Maximum dimensions: ' +
                    originalWidth +
                    '×' +
                    originalHeight,
            );
            return;
        }

        // Check if dimensions changed
        if (width === originalWidth && height === originalHeight) {
            setError('Please change at least one dimension');
            return;
        }

        setError('');
        setIsResizing(true);

        try {
            await onResize(width, height);
            onClose();
        } catch (err) {
            setError('Failed to resize image. Please try again.');
            setIsResizing(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 flex justify-center items-center z-50"
            onClick={(e) => {
                e.stopPropagation();
                if (!isResizing) onClose();
            }}
        >
            <div
                className="bg-background rounded-lg shadow-lg w-[500px] p-6"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h2 className="text-lg font-semibold tracking-tight text-foreground">
                            Resize Image
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            {asset.name}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isResizing}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-8">
                        <SmallLoader />
                    </div>
                ) : (
                    <>
                        {/* Original Dimensions */}
                        <div className="mb-6 p-4 bg-muted rounded-lg">
                            <p className="text-sm font-medium text-foreground">
                                Original Dimensions:{' '}
                                <span className="font-bold">
                                    {originalWidth} × {originalHeight}
                                </span>
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                Aspect Ratio:{' '}
                                {aspectRatio ? aspectRatio.toFixed(2) : 'N/A'}
                            </p>
                        </div>

                        {/* Dimension Inputs */}
                        <div className="space-y-4 mb-6">
                            <div className="flex items-center gap-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Width (px)
                                    </label>
                                    <input
                                        type="number"
                                        value={width}
                                        onChange={(e) =>
                                            handleWidthChange(
                                                parseInt(e.target.value) || 0,
                                            )
                                        }
                                        disabled={isResizing}
                                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                        min="1"
                                        max={originalWidth}
                                    />
                                </div>

                                <div className="flex items-end pb-2">
                                    <button
                                        onClick={() =>
                                            setAspectRatioLocked(
                                                !aspectRatioLocked,
                                            )
                                        }
                                        disabled={isResizing}
                                        className={cn(
                                            'p-2 rounded-md transition-colors',
                                            aspectRatioLocked
                                                ? 'bg-primary text-primary-foreground'
                                                : 'bg-muted text-muted-foreground',
                                        )}
                                        title={
                                            aspectRatioLocked
                                                ? 'Unlock aspect ratio'
                                                : 'Lock aspect ratio'
                                        }
                                    >
                                        {aspectRatioLocked ? (
                                            <Lock className="w-5 h-5" />
                                        ) : (
                                            <Unlock className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>

                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Height (px)
                                    </label>
                                    <input
                                        type="number"
                                        value={height}
                                        onChange={(e) =>
                                            handleHeightChange(
                                                parseInt(e.target.value) || 0,
                                            )
                                        }
                                        disabled={isResizing}
                                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                        min="1"
                                        max={originalHeight}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 rounded-md">
                                <p className="text-sm text-red-700 dark:text-red-400">
                                    {error}
                                </p>
                            </div>
                        )}

                        {/* Preview */}
                        <div className="mb-6 p-4 bg-muted rounded-lg">
                            <p className="text-sm font-medium text-foreground">
                                New Dimensions:{' '}
                                <span className="font-bold">
                                    {width} × {height}
                                </span>
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                New filename will be:{' '}
                                <span className="font-mono">
                                    {asset.name.replace(
                                        /(\.[^.]+)$/,
                                        `-${width}x${height}$1`,
                                    )}
                                </span>
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={onClose}
                                disabled={isResizing}
                                className="px-4 py-2 text-foreground bg-muted rounded-md hover:bg-muted/80 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleResize}
                                disabled={isResizing}
                                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                            >
                                {isResizing ? (
                                    <>
                                        <SmallLoader />
                                        <span>Resizing...</span>
                                    </>
                                ) : (
                                    'Resize'
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
